import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import { getLocalStorageCropExplorerPseudoUserId, useGetCropExploration, useModuleData, useUserContext } from "utils";

import { CropExplorationResponse, CropExplorationTemplateResponse, ExtendedCrop, PracticeCrop } from "models";

export type CropExplorationContextType = {
  cropExploration: CropExplorationResponse | CropExplorationTemplateResponse | null;
  setCropExplorationId: (id: string) => void;
  availableCrops: ExtendedCrop[];
  tourRefStep1ExampleProjectTab: React.RefObject<HTMLSpanElement>;
  tourRefStep2BauSection: React.RefObject<HTMLSpanElement>;
  tourRefStep3YearSection: React.RefObject<HTMLSpanElement>;
  tourRefStep4AddEditCrop: React.RefObject<HTMLDivElement>;
  tourRefStep5EmptyProjectTab: React.RefObject<HTMLSpanElement>;
};

const CropExplorationContext = createContext<CropExplorationContextType>({
  cropExploration: null,
  setCropExplorationId: () => null,
  availableCrops: [],
  tourRefStep1ExampleProjectTab: {} as React.RefObject<HTMLSpanElement>,
  tourRefStep2BauSection: {} as React.RefObject<HTMLSpanElement>,
  tourRefStep3YearSection: {} as React.RefObject<HTMLSpanElement>,
  tourRefStep4AddEditCrop: {} as React.RefObject<HTMLDivElement>,
  tourRefStep5EmptyProjectTab: {} as React.RefObject<HTMLSpanElement>,
});

export const useCropExplorationContext = () => useContext(CropExplorationContext);
export const CropExplorationContextProvider = ({ children, isAdmin }: { children: React.ReactNode; isAdmin?: boolean }) => {
  const pseudoUserId = getLocalStorageCropExplorerPseudoUserId();
  const { user } = useUserContext();
  const [cropExplorationId, setCropExplorationId] = useState<string | null>(null);
  const [cropExploration, setCropExploration] = useState<CropExplorationResponse | CropExplorationTemplateResponse | null>(null);
  const { data: cropExplorations } = useGetCropExploration({ isAdmin: !!isAdmin, user, pseudoUserId });
  const { crops } = useModuleData({
    moduleSlug: "crops",
    onFinish: () => {},
    isAdmin,
  });
  const [availableCrops, setAvailableCrops] = useState<ExtendedCrop[]>([]);

  // Tour steps
  const tourRefStep1ExampleProjectTab = useRef<HTMLSpanElement>();
  const tourRefStep2BauSection = useRef<HTMLSpanElement>();
  const tourRefStep3YearSection = useRef<HTMLSpanElement>();
  const tourRefStep4AddEditCrop = useRef<HTMLDivElement>();
  const tourRefStep5EmptyProjectTab = useRef<HTMLSpanElement>();

  useEffect(() => {
    if (!cropExplorations) return;
    if (!cropExplorationId) setCropExploration(cropExplorations[0]);
    const currentCropExploration = cropExplorations?.find((cropExploration) => cropExploration.id === cropExplorationId);
    if (currentCropExploration) setCropExploration(currentCropExploration);
  }, [cropExplorationId, cropExplorations, pseudoUserId]);

  useEffect(() => {
    if (!crops.crops || availableCrops.length > 0) return;

    const extendedCrops = crops.crops
      .filter((crop: PracticeCrop) => !crop.neutral)
      .map((crop: PracticeCrop) => {
        if (crops.topCrops?.some((topCrop: PracticeCrop) => crop.isMainCrop && topCrop.id === crop.id)) {
          return { ...crop, isTopCrop: true };
        }
        return crop;
      });

    setAvailableCrops(extendedCrops);
  }, [crops, availableCrops]);

  return (
    <CropExplorationContext.Provider
      value={{
        cropExploration: cropExploration,
        setCropExplorationId: setCropExplorationId,
        availableCrops: availableCrops,
        tourRefStep1ExampleProjectTab: tourRefStep1ExampleProjectTab as React.RefObject<HTMLSpanElement>,
        tourRefStep2BauSection: tourRefStep2BauSection as React.RefObject<HTMLDivElement>,
        tourRefStep3YearSection: tourRefStep3YearSection as React.RefObject<HTMLDivElement>,
        tourRefStep4AddEditCrop: tourRefStep4AddEditCrop as React.RefObject<HTMLDivElement>,
        tourRefStep5EmptyProjectTab: tourRefStep5EmptyProjectTab as React.RefObject<HTMLSpanElement>,
      }}
    >
      {children}
    </CropExplorationContext.Provider>
  );
};
